import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { company, colors } from '../../config/branding';
import { en } from '../../locale';

interface BackendConnectionBannerProps {
  onRetry: () => void;
  isRetrying?: boolean;
}

export default function BackendConnectionBanner({
  onRetry,
  isRetrying = false,
}: BackendConnectionBannerProps) {
  const theme = useTheme();
  const [dismissed, setDismissed] = useState(false);

  return (
    <Collapse in={!dismissed}>
      <Box
        role="alert"
        aria-live="polite"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9998,
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
            <Box
              sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 0.5 }}
            >
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  color: theme.palette.text.secondary,
                }}
              >
                {company.name}
              </Typography>
              <Typography
                component="span"
                sx={{ color: theme.palette.divider, fontSize: '0.65rem' }}
              >
                ·
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  color: theme.palette.text.primary,
                }}
              >
                SERVER UNAVAILABLE
              </Typography>
            </Box>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.85rem',
                lineHeight: 1.4,
              }}
            >
              {en.backend.unreachable}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Button
              size="small"
              variant="contained"
              disabled={isRetrying}
              startIcon={
                isRetrying ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />
              }
              onClick={onRetry}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: colors.primary,
                '&:hover': { bgcolor: '#1769c4' },
              }}
            >
              {isRetrying ? en.backend.reconnecting : en.common.retry}
            </Button>
            <Divider orientation="vertical" flexItem sx={{ borderColor: theme.palette.divider }} />
            <IconButton
              aria-label={en.backend.hideBanner}
              size="small"
              onClick={() => setDismissed(true)}
              sx={{ color: theme.palette.text.secondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Collapse>
  );
}
